import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileSearchIcon, ShieldCheckIcon, MenuIcon, XIcon, LogOutIcon, DownloadIcon, LockIcon, ClockIcon, ActivityIcon } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { Button } from '../ui/Button';
const regulatorNavItems = [{
  label: 'Audit Trail',
  path: ROUTES.REGULATOR,
  icon: FileSearchIcon
}, {
  label: 'Compliance Monitor',
  path: ROUTES.REGULATOR_COMPLIANCE,
  icon: ShieldCheckIcon
}];
// Live timestamp component
function LiveTimestamp() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  return <div className="flex items-center gap-2 text-body-sm text-surface-400" data-id="element-370">
      <motion.div animate={{
      opacity: [1, 0.5, 1]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-371">
        <ActivityIcon className="w-4 h-4 text-success-400" data-id="element-372" />
      </motion.div>
      <span data-id="element-373">Live: {time.toLocaleTimeString()}</span>
    </div>;
}
export function RegulatorLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsExporting(false);
  };
  return <div className="min-h-screen bg-surface-100" data-id="element-374">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-surface-900 border-b border-surface-700" data-id="element-375">
        <div className="flex items-center justify-between h-16 px-4" data-id="element-376">
          <Link to={ROUTES.REGULATOR} className="flex items-center gap-2" data-id="element-377">
            <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center" data-id="element-378">
              <ShieldCheckIcon className="w-5 h-5 text-surface-900" data-id="element-379" />
            </div>
            <span className="text-heading-md font-bold text-white" data-id="element-380">
              AeroZeep
            </span>
            <span className="text-caption font-semibold text-surface-900 bg-white px-2 py-0.5 rounded-md" data-id="element-381">
              Regulator
            </span>
          </Link>
          <motion.button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-white" whileTap={{
          scale: 0.9
        }} data-id="element-382">
            {isSidebarOpen ? <XIcon className="w-6 h-6" data-id="element-383" /> : <MenuIcon className="w-6 h-6" data-id="element-384" />}
          </motion.button>
        </div>
      </header>

      <div className="flex" data-id="element-385">
        {/* Sidebar */}
        <aside className={`
            fixed lg:sticky top-0 left-0 z-30
            w-72 h-screen bg-gradient-to-b from-surface-900 via-surface-900 to-surface-800 text-white
            transform transition-transform duration-300 ease-out lg:transform-none
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `} data-id="element-386">
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 h-16 px-6 border-b border-surface-700" data-id="element-387">
            <Link to={ROUTES.REGULATOR} className="flex items-center gap-3" data-id="element-388">
              <motion.div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center" whileHover={{
              scale: 1.05
            }} data-id="element-389">
                <ShieldCheckIcon className="w-6 h-6 text-surface-900" data-id="element-390" />
              </motion.div>
              <span className="text-heading-md font-bold" data-id="element-391">AeroZeep</span>
            </Link>
          </div>

          {/* Regulator Badge */}
          <div className="px-6 py-5 border-b border-surface-700 mt-16 lg:mt-0" data-id="element-392">
            <div className="flex items-center gap-3 mb-3" data-id="element-393">
              <div className="w-10 h-10 bg-surface-700 rounded-xl flex items-center justify-center" data-id="element-394">
                <ShieldCheckIcon className="w-5 h-5 text-surface-300" data-id="element-395" />
              </div>
              <div data-id="element-396">
                <p className="text-body-sm font-semibold text-white" data-id="element-397">
                  NCAA Auditor
                </p>
                <p className="text-caption text-surface-400" data-id="element-398">
                  Regulator Access
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-surface-800 rounded-lg" data-id="element-399">
              <LockIcon className="w-4 h-4 text-surface-400" data-id="element-400" />
              <span className="text-caption text-surface-400" data-id="element-401">
                Read-only access
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1" data-id="element-402">
            {regulatorNavItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return <motion.div key={item.path} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} data-id="element-403">
                  <Link to={item.path} onClick={() => setIsSidebarOpen(false)} className={`
                      relative flex items-center gap-3 px-4 py-3.5 rounded-xl text-body-md font-medium transition-all
                      ${active ? 'text-white' : 'text-surface-400 hover:bg-surface-800 hover:text-white'}
                    `} data-id="element-404">
                    {active && <motion.div layoutId="regulator-nav-indicator" className="absolute inset-0 bg-surface-700 rounded-xl" transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-405" />}
                    <span className="relative z-10 flex items-center gap-3" data-id="element-406">
                      <Icon className="w-5 h-5" data-id="element-407" />
                      {item.label}
                    </span>
                  </Link>
                </motion.div>;
          })}
          </nav>

          {/* Export Actions */}
          <div className="absolute bottom-24 left-0 right-0 px-4" data-id="element-408">
            <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-409">
              <Button variant="secondary" fullWidth icon={<DownloadIcon className="w-4 h-4" data-id="element-411" />} className="bg-surface-700 border-surface-600 text-white hover:bg-surface-600" onClick={handleExport} loading={isExporting} data-id="element-410">
                Export Report
              </Button>
            </motion.div>
          </div>

          {/* Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-surface-700" data-id="element-412">
            <Link to={ROUTES.HOME} className="flex items-center gap-3 px-4 py-3 rounded-xl text-body-md text-surface-400 hover:bg-surface-800 hover:text-white transition-colors" data-id="element-413">
              <LogOutIcon className="w-5 h-5" data-id="element-414" />
              Exit Portal
            </Link>
          </div>
        </aside>

        {/* Mobile Overlay */}
        <AnimatePresence data-id="element-415">
          {isSidebarOpen && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-20 lg:hidden" data-id="element-416" />}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex-1 min-h-screen" data-id="element-417">
          {/* Top Bar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 bg-white border-b border-surface-200 sticky top-0 z-20" data-id="element-418">
            <div className="flex items-center gap-4" data-id="element-419">
              <h1 className="text-heading-lg font-bold text-surface-900" data-id="element-420">
                {regulatorNavItems.find(item => isActive(item.path))?.label || 'Audit Trail'}
              </h1>
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-100 text-caption font-medium text-surface-600 rounded-lg" data-id="element-421">
                <LockIcon className="w-3.5 h-3.5" data-id="element-422" />
                Read-only access
              </span>
            </div>
            <div className="flex items-center gap-6" data-id="element-423">
              <LiveTimestamp data-id="element-424" />
              <div className="flex items-center gap-3" data-id="element-425">
                <div className="w-9 h-9 bg-surface-800 rounded-xl flex items-center justify-center" data-id="element-426">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-427" />
                </div>
                <div data-id="element-428">
                  <p className="text-body-sm font-semibold text-surface-900" data-id="element-429">
                    NCAA Auditor
                  </p>
                  <p className="text-caption text-surface-500" data-id="element-430">Regulator</p>
                </div>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="p-4 lg:p-8" data-id="element-431">
            <Outlet data-id="element-432" />
          </div>
        </main>
      </div>
    </div>;
}